/**
 * ChatPage — per-session component. One instance runs per visited chat session.
 *
 * ISOLATION GUARANTEE
 * ChatPage is never rendered directly at a route. It is rendered by SessionViewManager
 * via `sessionIds.map(id => <ChatPage key={id} .../>)`. React's `key` prop forces one
 * distinct fiber per session — each ChatPage instance has its own independent useState,
 * useChatStreaming, useArtifactStore, etc. hook state. State from session C1 can never
 * bleed into session C2.
 *
 * CONCURRENT STREAMING
 * useChatStreaming is called at the top of this component (above the isActive guard).
 * When isActive=false, ChatPage returns null — the UI is hidden but the component
 * stays MOUNTED. React continues running all hooks, so an in-progress stream keeps
 * writing tokens to this session's own chatMessages/artifactMap in the background.
 * Navigating back to this session reveals the fully accumulated state.
 *
 * EVA USAGE
 * EvaPlayground and EvaList render ChatPage directly (not via SessionViewManager)
 * with isActive=true. They manage their own session lifecycle.
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Button,
  useDisclosure,
  FormControl,
  FormLabel,
  Spinner,
  Text,
  VStack,
  Flex,
  Input,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  ModalCloseButton,
} from "@chakra-ui/react";
import ArtifactPanel from "modules/artifacts/ArtifactPanel";
import SplitView from "modules/common/SplitView";
import ChatContainer from "modules/chat/containers/Chat";
import { ArtifactOpenOpts, ChatMessage } from "modules/interfaces";
import {
  deleteChatSession as deleteChatSessionAPI,
  duplicateChatSession,
  fetchChatSession,
  refreshSystemPrompt,
  saveSession,
} from "services/AgentChatService";
import { getAnalyst } from "services/AnalystService";
import {
  AgentInputProto,
  ArtifactProto,
  ChatClientType,
  chatClientTypeFromJSON,
  chatClientTypeToJSON,
  ChatContext,
  ChatRequest,
  ExecutionStepProto,
} from "build-ts/chat_session";
import {
  artifactTypeFromJSON,
  ArtifactType,
  ChatMessageType,
  chatMessageTypeFromJSON,
  DarwinAgentType,
  DarwinHeader,
  ExecutionStatusType,
} from "build-ts/common";
import showToast from "hooks/useCustomToast";
import { useArtifactStore } from "modules/artifacts/hooks/useArtifactStore";
import { useArtifactPanel } from "modules/artifacts/hooks/useArtifactPanel";
import { useChatStreaming } from "modules/chat/hooks/useChatStreaming";
import { ArtifactKind, getArtifactType, resolveRegisteredArtifact } from "modules/artifacts/utils/artifactUtils";
import CopyButton from "components/common/CopyButton";
import { useChatContext } from "context/ChatContext";
import { useUIContext } from "context/UIContext";
import { useUser } from "context/UserContext";
import ChatPanelHeaderContainer from "modules/chat/containers/ChatPanelHeader";
import { HeaderWithStats } from "services/MetadataServices";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";

// This is used to clean the content of the chat messages from the ```markdown or ``` wrapper
const cleanContent = (raw: string): string => raw.replace(/^```(?:markdown)?\n?/, "").replace(/\n?```$/, "");

export interface ChatPageProps {
  /**
   * The session ID this instance manages. Stable for the lifetime of the component.
   * Provided by SessionViewManager (standard chat) or directly by Eva callers.
   */
  sessionId: string;
  /**
   * Whether this session is currently visible. When false the component returns null
   * but stays mounted so its hooks (streaming, artifacts) keep running in the background.
   */
  isActive: boolean;
  /** Analyst list loaded once by SessionViewManager and shared across all instances. */
  analystsList: HeaderWithStats[];
  isLoadingAnalysts: boolean;
  systemPrompt?: string;
  // Eva flags, true when rendered from any two components of Eva (both Saved and Unsaved chat)
  isFromEva?: boolean;
  initialChatSessionHeader?: DarwinHeader; // Session Header from EvaPlayground
  /**
   * Called by ChatPage after actions that change the EVA session list (rename, delete, save).
   * Provided by EvaListContainer to refresh its local session list; unused for FRIDA chats.
   */
  onEvaSessionsRefresh?: () => void;
  // Scenario creation mode (when set, chat is used for scenario creation with analyst/scenario context)
  /** The analyst id we navigated from (used to set selected analyst) */
  initialAnalystId?: string;
  // Selected scenario ids from the chat menu
  selectedScenarioIds?: string[];
  /** If set, chat will initialize a session and send this as the first user message */
  initialPrompt?: string;
}

const ChatPage: React.FC<ChatPageProps> = ({
  sessionId,
  isActive,
  analystsList,
  isLoadingAnalysts,
  systemPrompt = "",
  isFromEva = false,
  initialChatSessionHeader,
  onEvaSessionsRefresh,
  initialAnalystId: initialAnalystIdProp,
  initialPrompt: initialPromptProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation state set by NewChatPage for new sessions.
  const navState = location.state as {
    initialPrompt?: string;
    analystId?: string;
    isNewSession?: boolean;
    sessionId?: string;
    scenarioJobs?: ScheduledJobProto[];
    pendingMessage?: string;
    pendingCsvFiles?: { file: File; fileName: string }[];
  } | null;

  const initialPrompt = initialPromptProp || navState?.initialPrompt;
  // Initial analyst id from navigation state — auto-send guard waits until selectedAnalyst matches this
  const initialAnalystId = navState?.analystId || initialAnalystIdProp;

  const { defaultAnalystId, userId, defaultTimeZone } = useUser();
  // Chat session state from ChatContext
  const { setCurrentChatId, currentSessionHeader, setCurrentSessionHeader } = useChatContext();
  // Navbar collapse state from UIContext (app-wide)
  const { setIsNavbarCollapsed } = useUIContext();

  // ── Per-session state ────────────────────────────────────────────────────────
  // Loading state for chat
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  // System prompt modal state
  const {
    isOpen: isSystemPromptModalOpen,
    onOpen: onSystemPromptModalOpen,
    onClose: onSystemPromptModalClose,
  } = useDisclosure();
  // Save chat modal state
  const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  const [saveChatTitle, setSaveChatTitle] = useState("");
  const [saveChatDescription, setSaveChatDescription] = useState("");
  // Scenario jobs for the chat (loaded on session init + updated after manual/agent saves)
  const [scenarioJobs, setScenarioJobs] = useState<ScheduledJobProto[]>([]);
  // Track the last session ID we actually started loading to prevent duplicate fetches
  // when dependencies like analystsList change reference without the session ID changing.
  const lastFetchedSessionIdRef = useRef<string | null>(null);
  // Effective analyst id for scenario operations (from props or loaded chat session)
  const [resolvedAnalystId, setResolvedAnalystId] = useState<string | undefined>(initialAnalystIdProp);
  // Detect scenario jobs saved by the agent via tool calls (tool name includes "save_job")
  // in the last message of the response.
  const [scenarioJobsSavedByTools, setScenarioJobsSavedByTools] = useState<ScheduledJobProto[]>([]);
  // Detect workflow runs saved/updated by the agent via SAVE_WORKFLOW_RUN_TOOL.
  const [workflowRunsSavedByTools, setWorkflowRunsSavedByTools] = useState<ArtifactProto[]>([]);
  const [chatId, setChatId] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  // System prompt state - initialized from prop, can be updated from saved chat
  const [currentSystemPrompt, setCurrentSystemPrompt] = useState<string>(systemPrompt);
  // Chat is saved if session header has created_date
  const [isChatSaved, setIsChatSaved] = useState(false);
  const [selectedAnalyst, setSelectedAnalyst] = useState<HeaderWithStats | null>(null);

  // Ref populated by ChatContainer — lets ArtifactPanel (a sibling) send messages imperatively.
  const sendMessageRef = useRef<((msg: string, newChat: boolean, analyst: any) => void) | null>(null);

  // Artifact store: Map-based registry for artifact management
  const { artifactMap, registerArtifacts, updateArtifact, lookupByName, clearStore } = useArtifactStore();
  // Artifact panel: state + open-handlers
  const artifactPanel = useArtifactPanel({
    updateArtifact,
    onOpen: () => {
      if (isActive) setIsNavbarCollapsed(true);
    },
  });

  // ── handleArtifactClick — defined before useChatStreaming so it can be passed in ──
  // Stable wrapper forwarding to the panel hook; chatId is passed as a param so the
  // useCallback in useArtifactPanel never captures a stale chatId.
  // Prefer the already-hydrated artifact from the store over the stale original from
  // message data — this prevents redundant populate calls on every re-click.
  // Exception: WORKFLOW_RUN, WORKFLOW_RUN_SEARCH, and SCENARIO_JOB always arrive with
  // their full data (streamed, history-loaded, or freshly fetched from Newton via
  // get_scenario_detail / save_scenario). Skipping the store lookup prevents replacing
  // a freshly-returned artifact with the stale indexed version still in the store —
  // registerArtifacts runs in startTransition (deferred), so the store lags behind
  // the tool call result at the moment handleArtifactClick fires.
  const handleArtifactClick = useCallback(
    (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => {
      const type = getArtifactType(artifact);
      if (
        type === ArtifactType.WORKFLOW_RUN ||
        type === ArtifactType.WORKFLOW_RUN_SEARCH ||
        type === ArtifactType.SCENARIO_JOB
      ) {
        const latest =
          type === ArtifactType.WORKFLOW_RUN && opts?.subResourceName
            ? resolveRegisteredArtifact(artifact, lookupByName, {
                kind: opts?.kind as ArtifactKind | undefined,
                subResourceName: opts.subResourceName,
              })
            : artifact;
        artifactPanel.openArtifact(latest, chatId, opts?.activeTab, opts?.subResourceName);
        return;
      }
      const latest = resolveRegisteredArtifact(artifact, lookupByName, {
        kind: opts?.kind as ArtifactKind | undefined,
        subResourceName: opts?.subResourceName,
      });
      artifactPanel.openArtifact(latest, chatId, opts?.activeTab, opts?.subResourceName);
    },
    [chatId, lookupByName]
  );

  // ── Streaming hooks — MUST be above the isActive guard ──────────────────────
  // These hooks must survive isActive=false. When ChatPage returns null (inactive),
  // React keeps the component mounted and these hooks keep executing — so an
  // in-progress stream continues writing to this session's state in the background.
  const {
    isLoading,
    setIsLoading,
    streamingStatusLabel,
    setStreamingStatusLabel,
    prepareForNewTurn,
    finalizeStreamingTurn,
    appendStreamingToken,
    handleExecutionStep,
    handleReasoningToken,
    applyStreamChunk,
  } = useChatStreaming({
    setChatMessages,
    registerArtifacts,
    setScenarioJobsSavedByTools,
    setWorkflowRunsSavedByTools,
    onArtifactClick: handleArtifactClick,
  });

  // ── fetchChatHistory ─────────────────────────────────────────────────────────
  const fetchChatHistory = async (sid: string) => {
    // Fetch header only first to check client type — no messages, no transaction init.
    const headerResponse = await fetchChatSession(sid, false, false);
    const clientType = chatClientTypeFromJSON(headerResponse?.session_header?.type);
    const targetClientType = isFromEva ? ChatClientType.EVA : ChatClientType.FRIDA;
    const isMatchingType = clientType === targetClientType;

    let chatResponse;
    if (!isMatchingType) {
      // Client type doesn't match the current context — duplicate with the correct type.
      // e.g. FRIDA session opened in EVA context → duplicate as EVA, and vice versa.
      const duplicated = await duplicateChatSession({
        originalSessionId: sid,
        currentUserId: userId ?? "",
        clientType: chatClientTypeToJSON(targetClientType),
        save: false,
        initTransaction: true,
      });
      if (!duplicated?.session_header?.id) return;
      window.history.replaceState(null, "", `${isFromEva ? "/eva" : ""}/chat/${duplicated.session_header.id}`);
      chatResponse = duplicated;
      if (duplicated.analyst_id) {
        const jobs = await listScheduledJobsByOwnerId(duplicated.analyst_id, true);
        if (jobs && jobs.length > 0) {
          setScenarioJobs(jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
        }
      }
    } else {
      chatResponse = await fetchChatSession(sid, true, true);
      const analystId = chatResponse?.analyst_id;
      if (analystId) {
        const jobs = await listScheduledJobsByOwnerId(analystId, true);
        if (jobs && jobs.length > 0) {
          setScenarioJobs(jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
        }
      }
    }

    if (chatResponse?.session_header?.id) {
      setChatId(chatResponse.session_header.id);
      // Only update global ChatContext when this session is the foreground session.
      // Background sessions must not overwrite the active session's context values.
      if (isActive) {
        setCurrentChatId(chatResponse.session_header.id);
        // Stamp type from the known page context so downstream code never sees an
        // absent type field. The session's stored type may differ (e.g. old sessions
        // before client_type was tracked) — targetClientType is the authoritative value.
        setCurrentSessionHeader({
          ...chatResponse.session_header,
          type: chatClientTypeToJSON(targetClientType),
        });
      }
      if (chatResponse.session_header.created_date) {
        setIsChatSaved(true);
      }
    }

    // Resolve analyst id from existing chat session; keep it in sync when switching chats
    if (chatResponse?.analyst_id && chatResponse.analyst_id !== resolvedAnalystId) {
      setResolvedAnalystId(chatResponse.analyst_id);
    }

    // Check the status of the chat
    if (chatResponse?.status) {
      if (chatResponse.status === ExecutionStatusType.ERROR_STATE) {
        showToast("Error in Chat.", "Please start a new chat.", "error");
      }
    }

    // Extract system prompt and chat history from messages.
    let systemPromptFromMessage = "";
    const convertedMessages: ChatMessage[] = [];
    const collectedArtifacts: ArtifactProto[] = [];
    let pendingArtifacts: ArtifactProto[] = [];
    let pendingExecutionSteps: ExecutionStepProto[] = [];
    let langgraphMessageJsonList: string[] = [];
    // End-timestamp of the most recent message before the current REASONING block.
    // Used to compute the first reasoning step's duration on reload.
    let prevMsgEndMs: number | undefined;

    for (const msg of chatResponse?.messages ?? []) {
      const messageType = chatMessageTypeFromJSON(msg.message_type);

      // Extract system prompt from first SYSTEM message
      if (messageType === ChatMessageType.SYSTEM && msg.content && !systemPromptFromMessage) {
        systemPromptFromMessage = msg.content;
      } else if (messageType === ChatMessageType.HUMAN && (msg.content || (msg.execution_steps?.length ?? 0) > 0)) {
        // Reset pending buffers for the new AI turn.
        pendingExecutionSteps = [];
        pendingArtifacts = [];

        // Capture execution steps attached to the human message (e.g. CREATING_FILE_ARTIFACT).
        // These steps need to appear in the timeline before the reasoning steps.
        const humanSteps = msg.execution_steps ?? [];
        let fileUploadArtifacts: ArtifactProto[] = [];
        if (humanSteps.length > 0) {
          pendingExecutionSteps = humanSteps;
          const humanArtifacts = humanSteps.flatMap((s: ExecutionStepProto) => s.artifacts);
          pendingArtifacts = humanArtifacts;
          for (const artifact of humanArtifacts) {
            collectedArtifacts.push(artifact);
          }
          // Extract file artifacts for message chips and click-to-open.
          fileUploadArtifacts = humanSteps
            .filter((s: ExecutionStepProto) => s.status === ExecutionStatusType.CREATING_FILE_ARTIFACT)
            .flatMap((s: ExecutionStepProto) => s.artifacts);
        }

        // prevMsgEndMs: prefer the last human step's timestamp for accurate duration
        // computation of the first reasoning group; fall back to the message timestamp.
        const lastHumanStep = humanSteps[humanSteps.length - 1];
        prevMsgEndMs = lastHumanStep?.timestamp_ms
          ? Number(lastHumanStep.timestamp_ms)
          : msg.timestamp_ms
            ? Number(msg.timestamp_ms)
            : undefined;

        convertedMessages.push({
          author: "user",
          content: cleanContent(msg.content ?? ""),
          userTimestampMs: msg.timestamp_ms,
          ...(fileUploadArtifacts.length > 0 ? { fileArtifacts: fileUploadArtifacts } : {}),
        });
      } else if (messageType === ChatMessageType.REASONING) {
        // prevMsgEndMs is already set from the human message above.

        // Append reasoning steps after any human-message steps already in the buffer.
        const steps = msg.execution_steps;
        pendingExecutionSteps = [...pendingExecutionSteps, ...steps];
        const reasoningArtifacts = steps.flatMap(s => s.artifacts);
        pendingArtifacts = [...pendingArtifacts, ...reasoningArtifacts];
        langgraphMessageJsonList =
          msg.langgraph_message_json_list && msg.langgraph_message_json_list.length > 0
            ? msg.langgraph_message_json_list
            : [];

        for (const artifact of reasoningArtifacts) {
          collectedArtifacts.push(artifact);
        }
      } else if (messageType === ChatMessageType.RESPONSE && msg.content) {
        convertedMessages.push({
          author: "ai",
          content: cleanContent(msg.content),
          artifacts: pendingArtifacts.length > 0 ? pendingArtifacts : undefined,
          executionSteps: pendingExecutionSteps.length > 0 ? pendingExecutionSteps : undefined,
          prevMessageEndTimestampMs: prevMsgEndMs,
          ls_trace_url: msg.ls_trace_url,
          langgraph_message_json_list: langgraphMessageJsonList,
        });
        pendingArtifacts = [];
        pendingExecutionSteps = [];
        langgraphMessageJsonList = [];
      } else if (messageType === ChatMessageType.RESPONSE && msg.response_message) {
        convertedMessages.push({
          author: "ai",
          content: "",
          responseMessage: msg.response_message,
          artifacts: pendingArtifacts.length > 0 ? pendingArtifacts : undefined,
          executionSteps: pendingExecutionSteps.length > 0 ? pendingExecutionSteps : undefined,
          prevMessageEndTimestampMs: prevMsgEndMs,
          ls_trace_url: msg.ls_trace_url,
          langgraph_message_json_list: langgraphMessageJsonList,
        });
        pendingArtifacts = [];
        pendingExecutionSteps = [];
        langgraphMessageJsonList = [];
      }
    }
    // Update system prompt and context from API if found (for saved chats).
    // Always call setCurrentContext so stale context from a previous session is cleared
    // when the new session has no CONTEXT message.

    if (systemPromptFromMessage) {
      setCurrentSystemPrompt(systemPromptFromMessage);
    }
    setChatMessages(convertedMessages);
    // Register all collected artifacts into the store (deduplication handled by registerArtifacts).
    registerArtifacts(...collectedArtifacts);
  };

  // ── Session initialization (runs once on mount) ──────────────────────────────
  // navState and sessionId are captured from the closure at mount time — correct
  // because this ChatPage instance was just created in response to that navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // New session created by NewChatPage: navState carries everything we need — skip DB fetch.
    if (navState?.isNewSession && sessionId === navState?.sessionId) {
      setChatId(sessionId);
      // New sessions always mount as active, but guard defensively.
      if (isActive) setCurrentChatId(sessionId);
      setScenarioJobs(navState.scenarioJobs ?? []);
      if (navState.analystId) setResolvedAnalystId(navState.analystId);
      setIsLoadingChat(false);
      return;
    }

    // Existing session: fetch from DB.
    if (sessionId) {
      lastFetchedSessionIdRef.current = sessionId;
      setChatId(sessionId);
      setIsLoadingChat(true);
      fetchChatHistory(sessionId)
        .catch(err => {
          console.error("fetchChatHistory failed:", err);
          setChatMessages([]);
        })
        .finally(() => setIsLoadingChat(false));
    }
  }, []); // intentionally empty — runs exactly once per ChatPage instance

  // ── Eva unsaved chat init ────────────────────────────────────────────────────
  useEffect(() => {
    if (isFromEva && !isChatSaved && initialChatSessionHeader && !chatId) {
      setChatId(initialChatSessionHeader.id);
      setCurrentSessionHeader({
        ...initialChatSessionHeader,
        type: chatClientTypeToJSON(ChatClientType.EVA),
      });
    }
  }, [isFromEva, isChatSaved, initialChatSessionHeader, chatId]);

  // ── Clear artifacts panel when loading a new chat ───────────────────────────
  useEffect(() => {
    if (isLoadingChat) {
      artifactPanel.closePanel();
      artifactPanel.clearAll();
      clearStore();
    }
  }, [isLoadingChat, clearStore]);

  // ── Sync systemPrompt prop changes (Eva playground live-edit) ───────────────
  useEffect(() => {
    if (systemPrompt) {
      setCurrentSystemPrompt(systemPrompt);
    }
  }, [systemPrompt]);

  // ── Keep selectedAnalyst in sync with resolvedAnalystId ─────────────────────
  useEffect(() => {
    if (!resolvedAnalystId || analystsList.length === 0 || isFromEva) return;
    const analyst = analystsList.find(a => a.id === resolvedAnalystId);
    if (analyst) setSelectedAnalyst(analyst);
  }, [resolvedAnalystId, analystsList, isFromEva]);

  // ── Default analyst fallback for sessions without a resolved analyst ─────────
  useEffect(() => {
    if (selectedAnalyst || isFromEva || analystsList.length === 0 || resolvedAnalystId) return;
    const defaultAnalyst = analystsList.find(a => a.id === defaultAnalystId) || analystsList[0] || null;
    setSelectedAnalyst(defaultAnalyst);
  }, [analystsList, selectedAnalyst, isFromEva, resolvedAnalystId, defaultAnalystId]);

  // ── Merge agent-saved scenario jobs into the session list ───────────────────
  useEffect(() => {
    if (scenarioJobsSavedByTools.length === 0) return;
    setScenarioJobs(prev => {
      let updated = [...prev];
      for (const job of scenarioJobsSavedByTools) {
        if (!job.header?.id) continue;
        const idx = updated.findIndex(j => j.header?.id === job.header!.id);
        if (idx >= 0) {
          updated = [...updated.slice(0, idx), job, ...updated.slice(idx + 1)];
        } else {
          updated = [...updated, job];
        }
      }
      return updated;
    });
    for (const job of scenarioJobsSavedByTools) {
      updateArtifact({
        artifact_type: ArtifactType.SCENARIO_JOB,
        scenario_job: job,
      } as ArtifactProto);
    }
    for (const job of scenarioJobsSavedByTools) {
      artifactPanel.updateJobInPanel(job);
    }
    setChatMessages(prev =>
      prev.map(msg => {
        if (!msg.artifacts) return msg;
        const updatedArtifacts = msg.artifacts.map(a => {
          if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.SCENARIO_JOB) return a;
          const match = scenarioJobsSavedByTools.find(j => j.header?.id && j.header.id === a.scenario_job?.header?.id);
          return match ? { ...a, scenario_job: match } : a;
        });
        return { ...msg, artifacts: updatedArtifacts };
      })
    );
  }, [scenarioJobsSavedByTools]);

  // ── Sync workflow runs saved by tools into the open panel tab ────────────────
  // registerArtifacts is already called during streaming for all step artifacts,
  // so the store is up to date — only the panel tab needs to be patched.
  useEffect(() => {
    if (workflowRunsSavedByTools.length === 0) return;
    for (const artifact of workflowRunsSavedByTools) {
      artifactPanel.updateWorkflowRunInPanel(artifact);
    }
  }, [workflowRunsSavedByTools]);

  // ── Derived state ────────────────────────────────────────────────────────────
  // Set of job header ids already persisted (for Saved vs Draft badge on ScenarioArtifactCard)
  const savedJobIds = useMemo(() => {
    const ids = new Set<string>();
    scenarioJobs.forEach(job => {
      if (job.header?.id) ids.add(job.header.id);
    });
    return ids;
  }, [scenarioJobs]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  // Handle analyst selection — ChatPage only ever shows existing sessions, so changing
  // the analyst always opens a new chat tab with the chosen analyst pre-selected.
  const handleAnalystChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const analystId = e.target.value;
    // Existing chat: open a new tab with the chosen analyst pre-selected.
    // Do NOT update selectedAnalyst here — the current tab's dropdown must
    // continue showing the analyst that belongs to this conversation.
    window.open(`/chat/new?chatAnalystId=${analystId}`, "_blank");
  };

  // Populate a parent artifact in-place (for Inputs tab inline rendering) without opening a new tab.
  // Resolve the latest version from the store so populateSourceArtifact's already-hydrated
  // guard (`data_json` check) fires correctly and skips redundant network calls.
  const handlePopulateInput = useCallback(
    (artifact: ArtifactProto, opts?: ArtifactOpenOpts): Promise<void> => {
      const latest = resolveRegisteredArtifact(artifact, lookupByName, {
        kind: opts?.kind as ArtifactKind | undefined,
        subResourceName: opts?.subResourceName,
      });
      return artifactPanel.populateSourceArtifact(latest, chatId);
    },
    [chatId, lookupByName]
  );

  const handleToggleExpand = useCallback(() => {
    artifactPanel.setIsExpanded(v => !v);
  }, []);

  const handleRefreshSystemPrompt = useCallback(
    async (savedJob: ScheduledJobProto) => {
      if (!selectedAnalyst?.id || !currentSessionHeader) return;
      const analyst = await getAnalyst(selectedAnalyst.id);
      if (!analyst) return;
      // Merge the just-saved job into the current scenario list. scenarioJobs state
      // may not have re-rendered yet (React batching), so we patch inline using the
      // savedJob we already have rather than relying on the updated state value.
      const mergedJobs = scenarioJobs.map(j => (j.header?.id === savedJob.header?.id ? savedJob : j));
      const chatRequest = ChatRequest.create({
        session_header: currentSessionHeader,
        chat_context: ChatContext.create({
          agent_type: DarwinAgentType.ANALYST_CHAT_AGENT,
          agent_input: AgentInputProto.create({
            analyst,
            scenario_jobs: mergedJobs,
          }),
        }),
        is_save: true,
      });
      try {
        await refreshSystemPrompt(chatRequest);
      } catch {
        // toast already shown by refreshSystemPrompt service; don't re-throw.
      }
    },
    [selectedAnalyst?.id, currentSessionHeader, scenarioJobs]
  );

  // ── THE KEY GUARD — hooks above always run; only the UI is gated ─────────────
  // When isActive=false this ChatPage is invisible but still mounted. All React
  // state and effects above continue executing, so any in-progress stream keeps
  // writing tokens to this session's chatMessages/artifactMap without touching any
  // other session's state.
  if (!isActive) return null;

  return (
    <Box height="100%" width="100%" overflow="hidden" position="relative" display="flex" flexDirection="column">
      {/* Main Content Area */}
      <Box flex={1} width="100%" transition="margin-left 0.3s ease-in-out, width 0.3s ease-in-out" overflow="hidden">
        <SplitView
          leftPanel={
            <Box height="100%" width="100%" bg="bg.surface" overflow="hidden" display="flex" flexDirection="column">
              {currentSessionHeader && (
                <ChatPanelHeaderContainer
                  chatId={currentSessionHeader?.id || ""}
                  sessionHeader={currentSessionHeader}
                  setSessionHeader={setCurrentSessionHeader}
                  onAfterRename={() => {
                    if (isFromEva) onEvaSessionsRefresh?.();
                  }}
                  onDelete={() => {
                    if (!currentSessionHeader?.id) return;
                    const deletedId = currentSessionHeader.id;
                    deleteChatSessionAPI(deletedId).then(() => {
                      window.dispatchEvent(
                        new CustomEvent("chat:session-deleted", { detail: { sessionId: deletedId } })
                      );
                      if (isFromEva) {
                        onEvaSessionsRefresh?.();
                        navigate("/eva/agents");
                      } else {
                        navigate("/chat/new");
                      }
                    });
                  }}
                  onToggleArtifacts={() => artifactPanel.setIsVisible(v => !v)}
                  isArtifactsPanelVisible={artifactPanel.isVisible}
                  analystsList={analystsList}
                  selectedAnalyst={selectedAnalyst}
                  scenarioJobs={scenarioJobs}
                  isFromEva={isFromEva}
                  onViewPrompt={isFromEva ? onSystemPromptModalOpen : undefined}
                  isChatSaved={isChatSaved}
                  hasChatMessages={chatMessages.length > 0}
                  onSaveChat={isFromEva ? onSaveModalOpen : undefined}
                />
              )}
              <Box flex={1} overflow="hidden">
                <ChatContainer
                  userId={userId}
                  chatMessages={chatMessages}
                  chatId={chatId}
                  setChatId={setChatId}
                  setChatMessages={setChatMessages}
                  isLoadingChat={isLoadingChat}
                  sessionId={isFromEva && !isChatSaved ? initialChatSessionHeader?.id : sessionId}
                  onArtifactClick={handleArtifactClick}
                  selectedAnalyst={selectedAnalyst}
                  isFromEva={isFromEva}
                  cleanContent={cleanContent}
                  analystsList={analystsList}
                  isLoadingAnalysts={isLoadingAnalysts}
                  onAnalystChange={handleAnalystChange}
                  isArtifactsPanelVisible={artifactPanel.isVisible}
                  registerArtifacts={registerArtifacts}
                  lookupByName={lookupByName}
                  artifactMap={artifactMap}
                  isChatSaved={isChatSaved}
                  initialPrompt={initialPrompt}
                  initialAnalystId={initialAnalystId}
                  scenarioJobs={scenarioJobs}
                  setScenarioJobs={setScenarioJobs}
                  setScenarioJobsSavedByTools={setScenarioJobsSavedByTools}
                  // Streaming callbacks — ChatContainer receives these as props.
                  // useChatStreaming lives above the isActive guard so streams survive
                  // when this ChatPage is in the background (isActive=false).
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  streamingStatusLabel={streamingStatusLabel}
                  setStreamingStatusLabel={setStreamingStatusLabel}
                  prepareForNewTurn={prepareForNewTurn}
                  finalizeStreamingTurn={finalizeStreamingTurn}
                  appendStreamingToken={appendStreamingToken}
                  handleExecutionStep={handleExecutionStep}
                  handleReasoningToken={handleReasoningToken}
                  applyStreamChunk={applyStreamChunk}
                  sendMessageRef={sendMessageRef}
                />
              </Box>
            </Box>
          }
          rightPanel={
            <ArtifactPanel
              selectedArtifacts={artifactPanel.selectedArtifacts}
              isLoadingArtifact={artifactPanel.isLoadingArtifact}
              onClosePanel={artifactPanel.closePanel}
              onClearAll={artifactPanel.clearAll}
              onRemoveArtifact={artifactPanel.removeArtifact}
              savedJobIds={savedJobIds}
              onPopulateInput={handlePopulateInput}
              activateArtifact={artifactPanel.activateArtifact}
              artifactMap={artifactMap}
              lookupFn={lookupByName}
              onArtifactClick={handleArtifactClick}
              isArtifactExpanded={artifactPanel.isExpanded}
              onToggleExpand={handleToggleExpand}
              onArtifactTabViewChange={artifactPanel.updateViewState}
              defaultTimeZone={defaultTimeZone}
              chatId={chatId}
              analystList={analystsList}
              scenarioJobs={scenarioJobs}
              onJobSaved={job => {
                artifactPanel.updateJobInPanel(job);
                // Also patch scenarioJobs so workflow tag chips resolve the
                // correct name immediately, without waiting for the LLM to
                // call get_scenario_detail.
                if (job?.header?.id) {
                  setScenarioJobs(prev => {
                    const idx = prev.findIndex(j => j.header?.id === job.header!.id);
                    if (idx < 0) return [...prev, job];
                    return [...prev.slice(0, idx), job, ...prev.slice(idx + 1)];
                  });
                }
              }}
              onRefreshSystemPrompt={handleRefreshSystemPrompt}
            />
          }
          isRightPanelVisible={artifactPanel.isVisible}
          defaultRightWidth={45}
          expandRightPanel={artifactPanel.isExpanded}
        />
      </Box>

      {/* View prompt modal (Eva only) */}
      <Modal isOpen={isSystemPromptModalOpen} onClose={onSystemPromptModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>View prompt</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflow="hidden" display="flex" flexDirection="column">
            <Tabs size="sm" flex={1} display="flex" flexDirection="column" minH={0}>
              <TabList>
                <Tab>System prompt</Tab>
                <Tab>Context</Tab>
              </TabList>
              <TabPanels flex={1} overflow="auto" minH={0}>
                <TabPanel px={0} pt={3}>
                  <Flex justify="flex-end" mb={2}>
                    <CopyButton value={currentSystemPrompt} />
                  </Flex>
                  <Textarea
                    value={currentSystemPrompt}
                    readOnly
                    minH="400px"
                    resize="vertical"
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    fontFamily="mono"
                    fontSize="sm"
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Save Chat Modal (Eva only) */}
      <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} size="md" closeOnOverlayClick={!isSaving}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Chat</ModalHeader>
          {!isSaving && <ModalCloseButton />}
          <ModalBody>
            {isSaving ? (
              <VStack spacing={4} py={6}>
                <Spinner size="lg" color="green.500" thickness="3px" />
                <Text color="gray.600" fontSize="md">
                  Saving your chat...
                </Text>
              </VStack>
            ) : (
              <>
                <FormControl>
                  <FormLabel>Chat Title</FormLabel>
                  <Input
                    placeholder="Enter a title for this chat..."
                    value={saveChatTitle}
                    onChange={e => setSaveChatTitle(e.target.value)}
                    autoFocus
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Chat Description</FormLabel>
                  <Input
                    placeholder="Enter a description for this chat..."
                    value={saveChatDescription}
                    onChange={e => setSaveChatDescription(e.target.value)}
                  />
                </FormControl>
              </>
            )}
          </ModalBody>
          {!isSaving && (
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onSaveModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                isDisabled={!saveChatTitle.trim()}
                onClick={async () => {
                  if (!chatId || !saveChatTitle.trim()) return;
                  setIsSaving(true);
                  try {
                    // Save with title in single API call
                    await saveSession(chatId, saveChatTitle.trim(), saveChatDescription?.trim());
                    onSaveModalClose();
                    setSaveChatTitle("");
                    setSaveChatDescription("");
                    setCurrentSessionHeader(undefined);
                    setIsChatSaved(true);
                    if (isFromEva) {
                      onEvaSessionsRefresh?.();
                      if (chatId) {
                        navigate(`/eva/chat/${chatId}`);
                      }
                    }
                  } finally {
                    setIsSaving(false);
                  }
                }}
              >
                Save
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ChatPage;
